"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RequireRole } from "@/components/auth/require-role";
import {
  listGateways,
  createGateway,
  updateGateway,
  deleteGateway,
  type Gateway,
  type CreateGatewayRequest,
} from "@/lib/api";

interface GatewayFormState {
  name: string;
  dial_string: string;
  caller_ids: string;
  caller_id_prefix: string;
  enabled: boolean;
  weight: number;
  daily_limit: number;
}

const EMPTY_FORM: GatewayFormState = {
  name: "",
  dial_string: "",
  caller_ids: "",
  caller_id_prefix: "",
  enabled: true,
  weight: 1,
  daily_limit: 0,
};

export default function GatewaysPage() {
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Gateway | null>(null);
  const [form, setForm] = useState<GatewayFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Gateway | null>(null);

  const [reloadTick, setReloadTick] = useState(0);
  const load = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    listGateways().then((data) => {
      if (!cancelled) setGateways(data);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(gw: Gateway) {
    setEditing(gw);
    setForm({
      name: gw.name,
      dial_string: gw.dial_string,
      caller_ids: gw.caller_ids.join(", "),
      caller_id_prefix: gw.caller_id_prefix,
      enabled: gw.enabled,
      weight: gw.weight,
      daily_limit: gw.daily_limit,
    });
    setDialogOpen(true);
  }

  function validateForm(): CreateGatewayRequest | null {
    if (!form.name.trim()) {
      toast.error("线路名称必填");
      return null;
    }
    if (!form.dial_string.trim()) {
      toast.error("请填写拨号字符串");
      return null;
    }
    if (!form.dial_string.startsWith("sofia/external/") && !form.dial_string.startsWith("sofia/gateway/")) {
      toast.error("拨号字符串必须以 sofia/external/ 或 sofia/gateway/ 开头");
      return null;
    }
    const callerIds = form.caller_ids
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return {
      name: form.name.trim(),
      dial_string: form.dial_string.trim(),
      caller_ids: callerIds,
      caller_id_prefix: form.caller_id_prefix.trim(),
      enabled: form.enabled,
      weight: form.weight,
      daily_limit: form.daily_limit,
    };
  }

  async function handleSubmit() {
    const payload = validateForm();
    if (!payload) return;
    try {
      if (editing) {
        await updateGateway(editing.id, payload);
        toast.success("线路已更新");
      } else {
        await createGateway(payload);
        toast.success("线路已创建");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "操作失败");
    }
  }

  async function handleToggleEnabled(gw: Gateway, next: boolean) {
    try {
      await updateGateway(gw.id, { enabled: next });
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "更新失败");
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteGateway(deleteTarget.id);
      toast.success("线路已删除");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "删除失败");
    }
  }

  return (
    <div className="flex min-h-full flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">线路管理</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            拨号字符串支持两种格式：
            <code className="mx-1">sofia/external/&#123;dest&#125;@host:port</code>（IP 中继，使用 IP 白名单）或 <code className="mx-1">sofia/gateway/&lt;name&gt;/&#123;dest&#125;</code>（复用 FreeSWITCH 已注册的网关配置）。如需新注册 SIP 账号或新增网关，请联系平台管理员。
          </p>
        </div>
        <RequireRole roles={["owner"]}>
          <Button onClick={openCreate} size="sm">
            新建线路
          </Button>
        </RequireRole>
      </div>

      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-2 text-left font-medium">名称</th>
              <th className="px-4 py-2 text-left font-medium">拨号字符串</th>
              <th className="px-4 py-2 text-left font-medium">主叫号码</th>
              <th className="px-4 py-2 text-left font-medium">启用</th>
              <th className="px-4 py-2 text-left font-medium">权重</th>
              <th className="px-4 py-2 text-left font-medium">日限额</th>
              <th className="px-4 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {gateways.map((gw) => (
              <tr key={gw.id} className="border-b hover:bg-muted/20">
                <td className="px-4 py-2 font-medium">{gw.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{gw.dial_string}</td>
                <td className="px-4 py-2 text-xs">
                  {gw.caller_ids.length > 0 ? gw.caller_ids.join(", ") : <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-4 py-2">
                  <RequireRole
                    roles={["owner", "editor"]}
                    fallback={
                      gw.enabled ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          启用
                        </Badge>
                      ) : (
                        <Badge variant="secondary">已禁用</Badge>
                      )
                    }
                  >
                    <Switch checked={gw.enabled} onCheckedChange={(v) => handleToggleEnabled(gw, v)} />
                  </RequireRole>
                </td>
                <td className="px-4 py-2">{gw.weight}</td>
                <td className="px-4 py-2">{gw.daily_limit || "-"}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <RequireRole roles={["owner", "editor"]}>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(gw)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </RequireRole>
                    <RequireRole roles={["owner"]}>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(gw)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </RequireRole>
                  </div>
                </td>
              </tr>
            ))}
            {gateways.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  还没有线路。点击&ldquo;新建线路&rdquo;添加一条。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "编辑线路" : "新建线路"}</DialogTitle>
            <DialogDescription>配置 IP 中继参数。拨号字符串中的 <code>{"{dest}"}</code> 会被替换为目标号码。</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <Field label="名称">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="bjzixing"
              />
            </Field>
            <Field label="拨号字符串">
              <Input
                value={form.dial_string}
                onChange={(e) => setForm({ ...form, dial_string: e.target.value })}
                placeholder="sofia/external/{dest}@vos.your-trunk.com:5060"
                className="font-mono text-xs"
              />
            </Field>
            <Field label="主叫号码（逗号分隔，留空=仅 API 调用可用）">
              <Input
                value={form.caller_ids}
                onChange={(e) => setForm({ ...form, caller_ids: e.target.value })}
                placeholder="65121209, 65121210"
              />
            </Field>
            <Field label="号码前缀（可选）">
              <Input
                value={form.caller_id_prefix}
                onChange={(e) => setForm({ ...form, caller_id_prefix: e.target.value })}
                placeholder=""
              />
            </Field>
            <div className="flex items-center gap-4">
              <Field label="权重">
                <Input
                  type="number"
                  min={1}
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: Number(e.target.value) || 1 })}
                  className="w-24"
                />
              </Field>
              <Field label="日限额（0 = 不限）">
                <Input
                  type="number"
                  min={0}
                  value={form.daily_limit}
                  onChange={(e) => setForm({ ...form, daily_limit: Number(e.target.value) || 0 })}
                  className="w-28"
                />
              </Field>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">启用</label>
                <Switch
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleSubmit}>{editing ? "保存" : "创建"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除线路</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除线路 &ldquo;{deleteTarget?.name}&rdquo; 吗？使用该线路的在途任务会失败。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>删除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
